import { type KeyboardEvent, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Icon } from "@legal-500/legal500-design-system";
import type { ApiError } from "../../api/client";
import { powerlistEditions, powerlistLists, powerlistSeries } from "../../api/powerlist.service";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { useQuery } from "../../hooks/useApi";
import { ROUTES } from "../../routes";
import { EditionFormModal } from "./EditionFormModal";
import { EditionPublishActions } from "./EditionPublishActions";
import { ConfirmModal, usePowerlistAccess, useToast } from "./shared";
import { EditionQuestionsTab } from "./tabs/EditionQuestionsTab";
import { EntriesTab } from "./tabs/EntriesTab";
import { ListsTab } from "./tabs/ListsTab";
import { PageMappingsTab } from "./tabs/PageMappingsTab";
import { SponsorsTab } from "./tabs/SponsorsTab";
import styles from "./Powerlist.module.css";

const TABS = [
  { id: "entries", label: "Entries" },
  { id: "lists", label: "Lists" },
  { id: "pages", label: "Page mappings" },
  { id: "sponsors", label: "Sponsors" },
  { id: "questions", label: "Questions" },
] as const;

type TabId = (typeof TABS)[number]["id"];

const NOT_PREVIEWABLE_MESSAGE = "Add a region to this edition before previewing it on the site";

function previewFailureMessage(error: unknown): string {
  if ((error as ApiError | null)?.code === "GC_POWERLIST_NOT_PREVIEWABLE") {
    return NOT_PREVIEWABLE_MESSAGE;
  }
  return `Failed to open preview: ${error instanceof Error ? error.message : "Unknown error"}`;
}

function isTabId(value: string | null): value is TabId {
  return TABS.some(tab => tab.id === value);
}

export default function EditionDetailPage() {
  const { editionId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab: TabId = isTabId(requestedTab) ? requestedTab : "entries";
  const { canWrite, canDelete } = usePowerlistAccess();
  const { toast, success, failure } = useToast();
  const [editing, setEditing] = useState(false);
  const [previewOpening, setPreviewOpening] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, loading, error, refetch, isRefetching } = useQuery(async () => {
    const [edition, lists] = await Promise.all([
      powerlistEditions.get(editionId),
      powerlistLists.list(editionId),
    ]);
    const series = await powerlistSeries.get(edition.seriesId);
    return {
      edition,
      series,
      lists: [...lists].sort((a, b) => a.sortOrder - b.sortOrder),
    };
  }, [editionId]);

  function selectTab(id: TabId) {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set("tab", id);
      return next;
    });
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!offset) return;
    event.preventDefault();
    const next = TABS[(index + offset + TABS.length) % TABS.length];
    selectTab(next.id);
    document.getElementById(`powerlist-tab-${next.id}`)?.focus();
  }

  async function handlePreviewOnSite() {
    if (!editionId || previewOpening) return;
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
    }
    setPreviewOpening(true);
    try {
      const { url } = await powerlistEditions.createPublicPreviewLink(editionId);
      if (!previewWindow) {
        failure(new Error("Your browser blocked the preview window. Allow pop-ups and try again."));
        return;
      }
      previewWindow.location.href = url;
    } catch (err) {
      previewWindow?.close();
      failure(new Error(previewFailureMessage(err)));
    } finally {
      setPreviewOpening(false);
    }
  }

  if (loading && data === null) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Edition" />
        <LoadingState message="Loading edition..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Edition" />
        <ErrorBanner
          error={error?.message ?? "Edition not found"}
          onRetry={refetch}
          loading={isRefetching}
        />
      </div>
    );
  }

  const { edition, series, lists } = data;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={edition.title}
        subtitle={`${series.name} · ${edition.year} · ${edition.slug}`}
        meta={<StatusBadge status={edition.status} />}
      >
        <div className={styles.headerActions}>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={() => navigate(ROUTES.powerlist.seriesDetail(series.id))}
          >
            <Icon icon="arrow-left" size="small" />
            {series.name} editions
          </Button>
          <Link to={ROUTES.powerlist.preview(edition.id)}>Preview</Link>
          {canWrite && (
            <Button
              variant="outlined"
              size="small"
              onClick={handlePreviewOnSite}
              disabled={previewOpening}
              aria-busy={previewOpening}
            >
              {previewOpening ? "Opening preview…" : "Preview on site"}
            </Button>
          )}
          {canWrite && (
            <Button variant="outlined" size="small" onClick={() => setEditing(true)}>
              Edit edition
            </Button>
          )}
          {canDelete && (
            <Button variant="outlined" color="error" size="small" onClick={() => setDeleting(true)}>
              Delete edition
            </Button>
          )}
          <EditionPublishActions
            edition={edition}
            onTransitioned={(_edition, message) => {
              success(message);
              void refetch();
            }}
            onError={failure}
          />
        </div>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      <div className={styles.tabs} role="tablist" aria-label="Edition sections">
        {TABS.map((tab, index) => (
          <button
            key={tab.id}
            id={`powerlist-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`powerlist-tabpanel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            className={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => selectTab(tab.id)}
            onKeyDown={event => handleTabKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`powerlist-tabpanel-${activeTab}`}
        aria-labelledby={`powerlist-tab-${activeTab}`}
      >
        {activeTab === "entries" && (
          <EntriesTab editionId={edition.id} seriesId={edition.seriesId} lists={lists} />
        )}
        {activeTab === "lists" && (
          <ListsTab editionId={edition.id} lists={lists} onChanged={refetch} />
        )}
        {activeTab === "pages" && <PageMappingsTab editionId={edition.id} />}
        {activeTab === "sponsors" && <SponsorsTab editionId={edition.id} lists={lists} />}
        {activeTab === "questions" && <EditionQuestionsTab editionId={edition.id} />}
      </div>

      <EditionFormModal
        isOpen={editing}
        series={series}
        edition={edition}
        onClose={() => setEditing(false)}
        onSaved={saved => {
          setEditing(false);
          success(`${saved.title} saved`);
          void refetch();
        }}
      />
      <ConfirmModal
        isOpen={deleting}
        title="Delete edition"
        message={`Delete ${edition.title} with all its lists, sponsors and entries? This cannot be undone.`}
        confirmLabel="Delete edition"
        onClose={() => setDeleting(false)}
        onConfirm={async () => {
          await powerlistEditions.remove(edition.id);
          navigate(ROUTES.powerlist.seriesDetail(series.id));
        }}
      />
      {toast}
    </div>
  );
}
